import React, { useRef, useEffect, forwardRef } from 'react';
import '@material/web/button/filled-button.js';
import '@material/web/button/outlined-button.js';
import '@material/web/button/text-button.js';
import '@material/web/button/filled-tonal-button.js';
import '@material/web/iconbutton/icon-button.js';
import '@material/web/iconbutton/filled-icon-button.js';
import '@material/web/iconbutton/outlined-icon-button.js';
import '@material/web/textfield/outlined-text-field.js';
import '@material/web/textfield/filled-text-field.js';
import '@material/web/progress/linear-progress.js';
import '@material/web/progress/circular-progress.js';
import '@material/web/divider/divider.js';
import '@material/web/labs/card/elevated-card.js';
import '@material/web/labs/card/filled-card.js';
import '@material/web/labs/card/outlined-card.js';
import '@material/web/checkbox/checkbox.js';
import '@material/web/radio/radio.js';
import '@material/web/switch/switch.js';
import '@material/web/chips/chip-set.js';
import '@material/web/chips/assist-chip.js';
import '@material/web/chips/filter-chip.js';
import '@material/web/chips/input-chip.js';
import '@material/web/chips/suggestion-chip.js';

/**
 * Material Web Components 的 React 包装器
 * 这些组件使用 Custom Elements，需要通过 ref 和 event listeners 来交互
 */

// Button 包装器
export const MdFilledButton = forwardRef(({ children, onClick, disabled, ...props }, ref) => {
  const buttonRef = useRef(null);
  
  useEffect(() => {
    const button = buttonRef.current;
    if (button && onClick) {
      button.addEventListener('click', onClick);
      return () => button.removeEventListener('click', onClick);
    }
  }, [onClick]);
  
  useEffect(() => {
    if (buttonRef.current) {
      buttonRef.current.disabled = disabled;
    }
  }, [disabled]);

  return (
    <md-filled-button ref={buttonRef} {...props}>
      {children}
    </md-filled-button>
  );
});

export const MdOutlinedButton = forwardRef(({ children, onClick, disabled, ...props }, ref) => {
  const buttonRef = useRef(null);
  
  useEffect(() => {
    const button = buttonRef.current;
    if (button && onClick) {
      button.addEventListener('click', onClick);
      return () => button.removeEventListener('click', onClick);
    }
  }, [onClick]);
  
  useEffect(() => {
    if (buttonRef.current) {
      buttonRef.current.disabled = disabled;
    }
  }, [disabled]);

  return (
    <md-outlined-button ref={buttonRef} {...props}>
      {children}
    </md-outlined-button>
  );
});

export const MdTextButton = forwardRef(({ children, onClick, disabled, ...props }, ref) => {
  const buttonRef = useRef(null);
  
  useEffect(() => {
    const button = buttonRef.current;
    if (button && onClick) {
      button.addEventListener('click', onClick);
      return () => button.removeEventListener('click', onClick);
    }
  }, [onClick]);
  
  useEffect(() => {
    if (buttonRef.current) {
      buttonRef.current.disabled = disabled;
    }
  }, [disabled]);

  return (
    <md-text-button ref={buttonRef} {...props}>
      {children}
    </md-text-button>
  );
});

export const MdFilledTonalButton = forwardRef(({ children, onClick, disabled, ...props }, ref) => {
  const buttonRef = useRef(null);
  
  useEffect(() => {
    const button = buttonRef.current;
    if (button && onClick) {
      button.addEventListener('click', onClick);
      return () => button.removeEventListener('click', onClick);
    }
  }, [onClick]);
  
  useEffect(() => {
    if (buttonRef.current) {
      buttonRef.current.disabled = disabled;
    }
  }, [disabled]);

  return (
    <md-filled-tonal-button ref={buttonRef} {...props}>
      {children}
    </md-filled-tonal-button>
  );
});

// Icon Button 包装器
export const MdIconButton = forwardRef(({ children, onClick, disabled, selected, ...props }, ref) => {
  const buttonRef = useRef(null);
  
  useEffect(() => {
    const button = buttonRef.current;
    if (button && onClick) {
      button.addEventListener('click', onClick);
      return () => button.removeEventListener('click', onClick);
    }
  }, [onClick]);
  
  useEffect(() => {
    if (buttonRef.current) {
      buttonRef.current.disabled = disabled;
      if (selected !== undefined) {
        buttonRef.current.selected = selected;
      }
    }
  }, [disabled, selected]);

  return (
    <md-icon-button ref={buttonRef} {...props}>
      {children}
    </md-icon-button>
  );
});

export const MdFilledIconButton = forwardRef(({ children, onClick, disabled, ...props }, ref) => {
  const buttonRef = useRef(null);
  
  useEffect(() => {
    const button = buttonRef.current;
    if (button && onClick) {
      button.addEventListener('click', onClick);
      return () => button.removeEventListener('click', onClick);
    }
  }, [onClick]);
  
  useEffect(() => {
    if (buttonRef.current) {
      buttonRef.current.disabled = disabled;
    }
  }, [disabled]);

  return (
    <md-filled-icon-button ref={buttonRef} {...props}>
      {children}
    </md-filled-icon-button>
  );
});

// Text Field 包装器
export const MdOutlinedTextField = forwardRef(({ 
  value, 
  onChange, 
  onInput,
  label, 
  placeholder,
  type = 'text',
  disabled,
  error,
  errorText,
  supportingText,
  required,
  ...props 
}, ref) => {
  const fieldRef = useRef(null);
  
  useEffect(() => {
    const field = fieldRef.current;
    if (field) {
      const handleInput = (e) => {
        if (onChange) onChange(e);
        if (onInput) onInput(e);
      };
      field.addEventListener('input', handleInput);
      return () => field.removeEventListener('input', handleInput);
    }
  }, [onChange, onInput]);

  return (
    <md-outlined-text-field
      ref={fieldRef}
      value={value}
      label={label}
      placeholder={placeholder}
      type={type}
      disabled={disabled}
      error={error}
      error-text={errorText}
      supporting-text={supportingText}
      required={required}
      {...props}
    />
  );
});

// Progress 包装器
export const MdLinearProgress = forwardRef(({ value, max, indeterminate, ...props }, ref) => {
  const progressRef = useRef(null);
  
  useEffect(() => {
    if (progressRef.current) {
      progressRef.current.value = value;
      progressRef.current.max = max || 1;
      progressRef.current.indeterminate = indeterminate;
    }
  }, [value, max, indeterminate]);

  return <md-linear-progress ref={progressRef} {...props} />;
});

export const MdCircularProgress = forwardRef(({ value, max, indeterminate, ...props }, ref) => {
  const progressRef = useRef(null);
  
  useEffect(() => {
    if (progressRef.current) {
      progressRef.current.value = value;
      progressRef.current.max = max || 1;
      progressRef.current.indeterminate = indeterminate;
    }
  }, [value, max, indeterminate]);

  return <md-circular-progress ref={progressRef} {...props} />;
});

// Card 包装器
export const MdElevatedCard = forwardRef(({ children, ...props }, ref) => {
  return (
    <md-elevated-card ref={ref} {...props}>
      {children}
    </md-elevated-card>
  );
});

export const MdFilledCard = forwardRef(({ children, ...props }, ref) => {
  return (
    <md-filled-card ref={ref} {...props}>
      {children}
    </md-filled-card>
  );
});

export const MdOutlinedCard = forwardRef(({ children, ...props }, ref) => {
  return (
    <md-outlined-card ref={ref} {...props}>
      {children}
    </md-outlined-card>
  );
});

// Divider 包装器
export const MdDivider = forwardRef(({ ...props }, ref) => {
  return <md-divider ref={ref} {...props} />;
});

// 简化版的 MD3 Button - 与现有样式兼容的过渡组件
export const MdButton = ({ 
  children, 
  onClick, 
  disabled, 
  variant = 'filled',
  icon,
  style = {},
  className = ''
}) => {
  const baseStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    padding: '10px 24px',
    borderRadius: '20px',
    fontSize: '14px',
    fontWeight: '500',
    fontFamily: 'inherit',
    cursor: disabled ? 'not-allowed' : 'pointer',
    border: 'none',
    transition: 'all 0.2s ease',
    ...getVariantStyle(variant, disabled),
    ...style
  };

  function getVariantStyle(variant, disabled) {
    if (disabled) {
      return {
        background: 'var(--md-sys-color-surface-variant)',
        color: 'var(--md-sys-color-on-surface-variant)',
        opacity: 0.38
      };
    }
    
    switch (variant) {
      case 'filled':
        return {
          background: 'var(--md-sys-color-primary)',
          color: 'var(--md-sys-color-on-primary)'
        };
      case 'tonal':
        return {
          background: 'var(--md-sys-color-secondary-container)',
          color: 'var(--md-sys-color-on-secondary-container)'
        };
      case 'outlined':
        return {
          background: 'transparent',
          color: 'var(--md-sys-color-primary)',
          border: '1px solid var(--md-sys-color-outline)'
        };
      case 'text':
        return {
          background: 'transparent',
          color: 'var(--md-sys-color-primary)',
          padding: '10px 12px'
        };
      case 'error':
        return {
          background: 'var(--md-sys-color-error)',
          color: 'var(--md-sys-color-on-error)'
        };
      default:
        return {
          background: 'var(--md-sys-color-primary)',
          color: 'var(--md-sys-color-on-primary)'
        };
    }
  }

  return (
    <button 
      onClick={onClick} 
      disabled={disabled}
      style={baseStyle}
      className={className}
    >
      {icon && <span className="material-symbols-outlined" style={{ fontSize: 18 }}>{icon}</span>}
      {children}
    </button>
  );
};

// 简化版的 MD3 Icon Button
export const MdIconButtonSimple = ({ 
  icon, 
  onClick, 
  disabled, 
  title,
  style = {}
}) => {
  const buttonStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '40px',
    height: '40px',
    borderRadius: '50%',
    border: 'none',
    background: 'transparent',
    color: 'var(--md-sys-color-on-surface-variant)',
    cursor: disabled ? 'not-allowed' : 'pointer',
    transition: 'all 0.2s ease',
    opacity: disabled ? 0.38 : 1,
    ...style
  };

  return (
    <button 
      onClick={onClick} 
      disabled={disabled}
      style={buttonStyle}
      title={title}
      onMouseEnter={(e) => {
        if (!disabled) {
          e.currentTarget.style.background = 'var(--md-sys-color-surface-variant)';
          e.currentTarget.style.color = 'var(--md-sys-color-on-surface)';
        }
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = 'transparent';
        e.currentTarget.style.color = 'var(--md-sys-color-on-surface-variant)';
      }}
    >
      <span className="material-symbols-outlined" style={{ fontSize: 24 }}>{icon}</span>
    </button>
  );
};

// 简化版的 MD3 Card
export const MdCard = ({ 
  children, 
  variant = 'elevated',
  style = {},
  className = ''
}) => {
  const getVariantStyle = () => {
    switch (variant) {
      case 'filled':
        return {
          background: 'var(--md-sys-color-surface-variant)'
        };
      case 'outlined':
        return {
          background: 'var(--md-sys-color-surface)',
          border: '1px solid var(--md-sys-color-outline)'
        };
      case 'elevated':
      default:
        return {
          background: 'var(--md-sys-color-surface)',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
        };
    }
  };

  const cardStyle = {
    borderRadius: '12px',
    padding: '16px',
    ...getVariantStyle(),
    ...style
  };

  return (
    <div style={cardStyle} className={className}>
      {children}
    </div>
  );
};

// 状态指示器组件
export const MdStatusIndicator = ({ 
  status, // 'online' | 'offline' | 'warning' | 'pending'
  size = 8,
  pulse = false
}) => {
  const getColor = () => {
    switch (status) {
      case 'online':
      case 'success':
        return '#4CAF50';
      case 'offline':
      case 'error':
        return '#F44336';
      case 'warning':
        return '#FF9800';
      case 'pending':
        return '#2196F3';
      default:
        return '#9E9E9E';
    }
  };

  const style = {
    width: size,
    height: size,
    borderRadius: '50%',
    background: getColor(),
    display: 'inline-block',
    animation: pulse ? 'pulse 2s infinite' : 'none'
  };

  return <span style={style} />;
};

// 空状态组件
export const MdEmptyState = ({ 
  icon,
  title,
  description,
  action
}) => {
  const style = {
    textAlign: 'center',
    padding: '40px 20px',
    color: 'var(--md-sys-color-on-surface-variant)'
  };

  return (
    <div style={style}>
      {icon && (
        <span 
          className="material-symbols-outlined" 
          style={{ fontSize: 64, marginBottom: 16, opacity: 0.5 }}
        >
          {icon}
        </span>
      )}
      {title && <div style={{ fontSize: 16, marginBottom: 8 }}>{title}</div>}
      {description && (
        <div style={{ fontSize: 14, marginBottom: 16, opacity: 0.8 }}>{description}</div>
      )}
      {action}
    </div>
  );
};

// 加载状态组件
export const MdLoading = ({ text = '加载中...' }) => {
  const style = {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '20px',
    gap: '8px',
    color: 'var(--md-sys-color-on-surface-variant)'
  };

  return (
    <div style={style}>
      <span className="material-symbols-outlined" style={{ animation: 'spin 1s linear infinite' }}>
        refresh
      </span>
      <span>{text}</span>
    </div>
  );
};
